import * as z from "zod";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "../../../providers/form";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import GenericButton from "widgets/controls/buttons/generic";
import ComboBox from "widgets/shared/inputs/combobox";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { accountSiteList } from "store/appSelectors";
import { useEffect, useState } from "react";
import MultiCombobox from "widgets/shared/inputs/multiselectCombobox";
import { DeviceListReqBody } from "api/types/device";
import { deviceList } from "api/device";
import { setLoading } from "store/appSlice";
import { ReportConfiguration } from "models/reports/configuration";
import useWhiteLabel from "whitelabel/useWhiteLabel";

// Schema
const BuildSchema = z
  .object({
    reportType: z
      .number()
      .min(1, { message: i18next.t("zod.error.report.type") }),
    site: z.string().min(1, { message: i18next.t("zod.error.site") }),
    device: z.string().optional(),
    devices: z.array(z.string()).optional(),
    timezone: z.number().optional(),
    all: z.boolean().default(false).optional(),
    models: z.array(z.string()).optional(),
    deviceModel: z.string().optional(),
    isTest: z.boolean().default(true),
  })
  .refine(
    (data) => {
      if (data.reportType === 2 || data.reportType === 3) {
        return data.devices && data.devices.length > 0;
      }
      if (data.reportType === 1) {
        return data.device && data.device.length > 0;
      }
      return true;
    },
    {
      message: i18next.t("zod.error.report.type"),
      path: ["device", "devices"],
    }
  );

export type BuildFormType = z.infer<typeof BuildSchema>;

// Props
type BuildFormProps = {
  submit: (data: BuildFormType) => void;
  disabled?: boolean;
  onTouched?: () => void;
};

const BuildForm = ({
  submit,
  disabled = false,
  onTouched = () => {},
}: BuildFormProps) => {
  const { availableReports } = useWhiteLabel();
  const form = useForm<BuildFormType>({
    resolver: zodResolver(BuildSchema),
  });
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const siteList = useSelector(accountSiteList, shallowEqual);
  const [devList, setDevList] = useState([]);
  const [customError, setCustomError] = useState<string | null>(null);
  const [gLoading, setDevLoading] = useState(false);

  const { errors } = form.formState;

  const [selectedReportConfig, setSelectedReportConfig] = useState(null);

  // Filter ReportConfiguration based on availableReports (exclude adhoc-only system reports)
  const filteredReportConfig = ReportConfiguration.filter(
    (r) => availableReports?.includes(r.id) && !r.systemReport
  );

  const onSubmit = async (formData: BuildFormType) => {
    try {
      await submit(formData);
    } catch (error) {
      setCustomError(t("form.error.unknown"));
    }
  };

  const clearErrors = () => {
    form.clearErrors();
    setCustomError(null);
  };

  const fetchDeviceList = async (siteId: string) => {
    if (!siteId) return;
    clearErrors();
    form.setValue("device", "");
    form.setValue("devices", []);
    form.setValue("deviceModel", undefined);

    setDevLoading(true);
    const body: DeviceListReqBody = {
      classId: siteId,
      sort: "snr",
      desc: true,
      installed: "INSTALLED",
      models: selectedReportConfig?.models,
    };
    dispatch(setLoading(true));
    const { data, isLoading } = await deviceList(body);
    dispatch(setLoading(isLoading));
    setDevLoading(isLoading);

    if (data) {
      setDevList(data);
      if (data.length === 0) setCustomError(t("zod.error.no.devices"));
    }
  };

  useEffect(() => {
    // Clear device selection when reportType changes
    form.setValue("device", "");
    form.setValue("devices", []);
    form.setValue("deviceModel", undefined);
    form.setValue("site", "");
  }, [selectedReportConfig]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-6">
        <div className="grid grid-cols-1 gap-6">
          <FormField
            control={form.control}
            name="reportType"
            rules={{ required: true }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">
                  {t("form.input.select.report.type")}
                </FormLabel>
                <FormControl>
                  <ComboBox
                    items={filteredReportConfig}
                    placeholder={t("form.placeholder.select.report.type")}
                    id="report-type"
                    labelKey="name"
                    valueKey="id"
                    state={errors.reportType ? "error" : undefined}
                    onFocus={() => {
                      clearErrors();
                      onTouched();
                    }}
                    onBlur={() => {
                      onTouched();
                    }}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      const config = filteredReportConfig.find(
                        (item) => item.id === value
                      );
                      setSelectedReportConfig(config);
                      form.setValue("models", config?.models || []);
                    }}
                    clearIcon={false}
                    sizes="md"
                    color="secondary"
                    variant="solid"
                    disabled={disabled}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="site"
            rules={{ required: true }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-white">
                  {t("form.input.select.site")}
                </FormLabel>
                <FormControl>
                  <ComboBox
                    items={siteList}
                    placeholder={t("form.placeholder.select.site")}
                    id="site"
                    labelKey="name"
                    valueKey="id"
                    value={field.value}
                    state={errors.site ? "error" : undefined}
                    onFocus={() => {
                      clearErrors();
                      onTouched();
                    }}
                    onBlur={() => {
                      onTouched();
                    }}
                    onChange={(value) => {
                      field.onChange(value);
                      fetchDeviceList(value);
                    }}
                    filter
                    sizes="md"
                    color="secondary"
                    variant="solid"
                    disabled={disabled}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {form.watch("site") &&
            !gLoading &&
            selectedReportConfig?.multiSelect &&
            devList.length > 0 && (
              <FormField
                control={form.control}
                name="devices"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">
                      {t("form.input.select.devices")}
                    </FormLabel>
                    <FormControl>
                      <MultiCombobox
                        items={devList}
                        placeholder={t("form.placeholder.select.devices")}
                        id="devices"
                        labelKey="name"
                        valueKey="snr"
                        value={field.value}
                        state={errors.devices ? "error" : undefined}
                        onFocus={() => {
                          clearErrors();
                          onTouched();
                        }}
                        onBlur={() => {
                          onTouched();
                        }}
                        onChange={(value) => {
                          field.onChange(value);
                          if (value.length === devList.length) {
                            form.setValue("all", true);
                          }
                          // Auto-set timezone when first device is selected
                          if (value && value.length > 0) {
                            const firstSelectedDevice = devList.find(
                              (device) => device.snr === value[0]
                            );
                            if (
                              firstSelectedDevice &&
                              firstSelectedDevice.timezone !== undefined
                            ) {
                              form.setValue(
                                "timezone",
                                firstSelectedDevice.timezone
                              );
                            }
                            form.setValue(
                              "deviceModel",
                              firstSelectedDevice?.model
                            );
                          } else {
                            form.setValue("deviceModel", undefined);
                          }
                        }}
                        filter
                        selectAll
                        sizes="md"
                        color="secondary"
                        variant="solid"
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

          {form.watch("site") &&
            !gLoading &&
            selectedReportConfig?.singleSelect &&
            devList.length > 0 && (
              <FormField
                control={form.control}
                name="device"
                rules={{ required: true }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">
                      {t("form.input.select.device")}
                    </FormLabel>
                    <FormControl>
                      <ComboBox
                        items={devList}
                        placeholder={t("form.placeholder.select.device")}
                        id="device"
                        labelKey="name"
                        valueKey="snr"
                        value={field.value}
                        state={errors.device ? "error" : undefined}
                        onFocus={() => {
                          clearErrors();
                          onTouched();
                        }}
                        onBlur={() => {
                          onTouched();
                        }}
                        onChange={(value) => {
                          field.onChange(value);

                          // Auto-set timezone when device is selected
                          if (value) {
                            const selectedDevice = devList.find(
                              (device) => device.snr === value
                            );
                            if (
                              selectedDevice &&
                              selectedDevice.timezone !== undefined
                            ) {
                              form.setValue(
                                "timezone",
                                selectedDevice.timezone
                              );
                            }
                            form.setValue(
                              "deviceModel",
                              selectedDevice?.model
                            );
                          } else {
                            form.setValue("deviceModel", undefined);
                          }
                        }}
                        sizes="md"
                        color="secondary"
                        filter
                        variant="solid"
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

          {(customError || Object.values(errors).length > 0) && (
            <p className={`mt-0 text-txt-md text-amber-300`}>
              {customError ||
                Object.values(errors)[0]?.message ||
                t("zod.error.form.fill")}
              *
            </p>
          )}
        </div>
        <div className="mt-12">
          <GenericButton
            type="submit"
            text={"Build"}
            extra="w-full"
            color="white"
            size="md"
            disabled={disabled}
          />
        </div>
      </form>
    </Form>
  );
};

export default BuildForm;
